import { useEffect, useRef, useState } from 'react';
import promotionsService from '../services/promotionsService';
import { getTenantConfig } from '../config/tenantConfig';
import { Promotion, PromotionSurface } from '../types/promotion';

interface CacheEntry {
  fetched_at: number;
  promotion: Promotion | null;
}

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, CacheEntry>();

interface UsePromotionResult {
  promotion: Promotion | null;
  loading: boolean;
}

/**
 * Fetches the active promotion for the current tenant + given surface.
 * Caches per `${tenant}:${surface}` for 60s in module memory so multiple
 * components on the same page share one network call.
 *
 * Silently returns null on error or 204 — never throws to render path.
 */
export function usePromotion(surface: PromotionSurface): UsePromotionResult {
  const tenant = getTenantConfig().id;
  const cacheKey = `${tenant}:${surface}`;

  // Seed initial state from cache so the first paint already has the promotion
  // when navigating between marketplace pages.
  const cached = cache.get(cacheKey);
  const cacheFresh = cached && Date.now() - cached.fetched_at < CACHE_TTL_MS;
  const [promotion, setPromotion] = useState<Promotion | null>(
    cacheFresh ? cached.promotion : null,
  );
  const [loading, setLoading] = useState<boolean>(!cacheFresh);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const cur = cache.get(cacheKey);
    if (cur && Date.now() - cur.fetched_at < CACHE_TTL_MS) {
      setPromotion(cur.promotion);
      setLoading(false);
      return;
    }
    setLoading(true);
    promotionsService.getActivePromotion(tenant, surface)
      .then((promo) => {
        cache.set(cacheKey, { fetched_at: Date.now(), promotion: promo });
        if (mounted.current) {
          setPromotion(promo);
          setLoading(false);
        }
      })
      .catch(() => {
        // getActivePromotion already swallows errors; just bail
        if (mounted.current) {
          setPromotion(null);
          setLoading(false);
        }
      });
  }, [cacheKey, tenant, surface]);

  return { promotion, loading };
}

/** Test/util — drop the cached promotion so a refetch happens on next mount. */
export function invalidatePromotionCache(tenant?: string, surface?: PromotionSurface) {
  if (tenant && surface) {
    cache.delete(`${tenant}:${surface}`);
  } else {
    cache.clear();
  }
}
